import React, { useEffect, useMemo, useState } from "react";
import { Form, Row, Col, DatePicker, Typography, Table, Select, message } from 'antd';
import { getRangeOptions, getPaginationDetails, getDefaultPageSize } from './reports.module';
import { fetchBlockedSheet } from './../../actions/reports.action';
import { getAPIDateFormat, convertDateStringToDate, getLocalDate, getLocalDateAndTime } from './../../utils/date.utils';
import { isValid, isValidArray } from "../../utils/utilities";
import { Printer } from './../../components/custom-components/custom-component';
import { getVechicles } from './../../components/edit-vechicle-component/edit-vechicle.module';
import { loadingAction } from './../../actions/loading-actions';
import { useDispatch } from "react-redux";
import * as XLSX from 'xlsx';
import './blocket-seats.report.scss';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const both = 'Blocked and Released';
const allowedMaxDaysRange = 31;
const xs = 24, sm = 24, md = 12, lg = 12, xl = 12, xxl = 12;

// ============================================================
// Formatters
// ============================================================
const fmtINR = (n) => {
    if (n == null || isNaN(n)) return '—';
    const num = Number(n);
    const hasDecimal = num % 1 !== 0;
    return '₹' + num.toLocaleString('en-IN', hasDecimal ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {});
};
const fmtINRpaise = (n) => {
    if (n == null || isNaN(n)) return '—';
    return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const getChannelClass = (name) => {
    if (!name) return 'ch-web';
    const n = String(name).toLowerCase();
    if (n.includes('redbus')) return 'ch-redbus';
    if (n.includes('abhibus')) return 'ch-abhibus';
    if (n.includes('paytm')) return 'ch-paytm';
    if (n.includes('website')) return 'ch-website';
    return 'ch-web';
};

// ============================================================
// Inline icons (match the sales report mockup)
// ============================================================
const Icon = {
    search: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="7" cy="7" r="5"/><path d="m11 11 3 3"/></svg>,
    download: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M8 2v9m-3-3 3 3 3-3M2 13h12"/></svg>,
    caretRight: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m6 4 4 4-4 4"/></svg>,
    run: <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5 3l8 5-8 5z"/></svg>,
};

// ============================================================
// Preserved helpers (untouched logic — drive filtering, Excel export, print)
// ============================================================
const normalizeToArray = (val) => {
    if (val == null) return null;
    return Array.isArray(val) ? val : [val];
};

const getSchedules = (data) => {
    let formatedData = [];
    if (isValidArray(data) && data.length > 0) {
        data.forEach(element => {
            if (isValid(element.description) && formatedData.indexOf(element.description) === -1) {
                formatedData.push(element.description);
            }
        });
    }
    return formatedData;
};

const getFilterDetails = ({ scheduleDesc, agentName, travellerStatus }, data) => {
    const scheduleList = normalizeToArray(scheduleDesc);
    const agentList = normalizeToArray(agentName);
    const allSchedules = !scheduleList || scheduleList.length === 0;
    const allAgents = !agentList || agentList.length === 0;

    let formatedData = [];
    if (isValidArray(data) && data.length > 0) {
        data.forEach((element) => {
            const scheduleOk = allSchedules || scheduleList.includes(element.description);
            const agentOk = allAgents || agentList.includes(element.name);
            if (scheduleOk && agentOk && (travellerStatus == both || travellerStatus == element.traveller_status)) {
                formatedData.push(element);
            }
        });
    }
    return formatedData;
};

// ----- Columns used for Excel export + the print preview (full detail) -----
const getBlockedTicketsColumns = (page, paginationSize, vehicleNumberById = {}) => [
    { title: 'SN', key: 'index', render: (text, record, index) => (page - 1) * paginationSize + index + 1 },
    {
        title: 'Travel date', key: 'start_date', dataIndex: 'start_date',
        render: (text, record) => isValid(record.start_date) ? record.start_date.split('T')[0] : '',
    },
    { title: 'PNR', key: 'pnr', dataIndex: 'pnr' },
    { title: 'Seat nos', key: 'seat_number', dataIndex: 'seat_number' },
    { title: 'Fare', key: 'discounted_fare', dataIndex: 'discounted_fare' },
    { title: 'Agency', key: 'name', dataIndex: 'name' },
    { title: 'Email', key: 'email_id', dataIndex: 'email_id' },
    {
        title: 'Booking time', key: 'booking_time', dataIndex: 'booking_time',
        render: (text, record) => isValid(record.booking_time) ? getLocalDateAndTime(record.booking_time) : '',
    },
    { title: 'Phone No', key: 'phone_number', dataIndex: 'phone_number' },
    { title: 'Trip description', key: 'description', dataIndex: 'description' },
    {
        title: 'Vehicle number', key: 'vehicle_number', dataIndex: 'vehicle_id',
        render: (text, record) => vehicleNumberById[record.vehicle_id] || '',
    },
    {
        title: 'Release time', key: 'release_time', dataIndex: 'release_time',
        render: (text, record) => isValid(record.release_time) ? getLocalDate(convertDateStringToDate(record.release_time.split('T')[0])) : '',
    },
    {
        title: 'Released by', key: 'updated_by', dataIndex: 'updated_by',
        render: (text, record) => isValid(record.updated_by) ? record.updated_by : '',
    },
    ...(global.useRemarks === 1
        ? [{ title: 'Remarks', key: 'remarks', dataIndex: 'remarks', render: (text) => text || '' }]
        : []),
];

const generateJsonToExport = (filters, filteredBlockedTickets, blockedTicketsColumns) => {
    let reportsBlockedXmlData = [];
    if (isValidArray(filteredBlockedTickets) && filteredBlockedTickets.length > 0) {
        filteredBlockedTickets.forEach((element, index) => {
            let rowData = [];
            blockedTicketsColumns.forEach(column => {
                if (column.key === 'index') {
                    rowData.push(index + 1);
                } else if (column.render) {
                    rowData.push(column.render(element[column.dataIndex], element, index));
                } else {
                    rowData.push(element[column.dataIndex] || '');
                }
            });
            reportsBlockedXmlData.push(rowData);
        });
    }

    const agentList = normalizeToArray(filters.agentName);
    const agentName =
        !agentList || agentList.length === 0 || filters.isSelectAllAgents
            ? 'all'
            : agentList.length === 1
                ? agentList[0]
                : agentList.join(', ');
    let dateType = filters.isBookingDate ? "Booking" : "Travel";
    let generatedTime = getLocalDateAndTime(new Date());

    const columnHeaders = blockedTicketsColumns.map(column => column.title);

    var worksheet = XLSX.utils.aoa_to_sheet([
        [],
        [],
        ["Report", "Agents", "From Date", "To Date", "Type of date", "Generated day/time"],
        ["Blocking report", agentName, filters.fromDate, filters.toDate, dateType, generatedTime],
        [],
        [],
        columnHeaders,
        ...reportsBlockedXmlData
    ]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Block Seats Report');
    XLSX.writeFile(workbook, 'block-seats-report.xlsx');
};

const getStatusMeta = (record) => {
    const raw = String(record.traveller_status || '').toLowerCase();
    if (raw.includes('release')) return { cls: 'released', label: record.traveller_status || 'Released' };
    return { cls: 'blocked', label: record.traveller_status || 'Blocked' };
};

// ============================================================
// Sub-components
// ============================================================
function PageHeader({ onExport, canExport, printerNode }) {
    return (
        <div className="pageheader">
            <div>
                <div className="breadcrumb">
                    <span>Reports</span>{Icon.caretRight}<span style={{ color: 'var(--ink-2)' }}>Blocked tickets report</span>
                </div>
                <h1>Blocked Tickets Report</h1>
            </div>
            <div className="pageheader-actions">
                {printerNode}
                <button type="button" className="btn primary" disabled={!canExport} onClick={onExport}>
                    {Icon.download}Export
                </button>
            </div>
        </div>
    );
}

function FilterCard({ form, dateType, setDateType, onSubmit, onReset, running }) {
    const handleSubmit = (values) => onSubmit({ ...values, isBooking: dateType });

    return (
        <div className="card">
            <Form form={form} onFinish={handleSubmit}>
                <div className="filter-grid">
                    <div className="field" style={{ gridColumn: '1 / span 3', gridRow: 1 }}>
                        <div className="field-label">Date type</div>
                        <div className="segmented">
                            <button type="button"
                                className={dateType === true ? 'active' : ''}
                                onClick={() => setDateType(true)}>Booking</button>
                            <button type="button"
                                className={dateType === false ? 'active' : ''}
                                onClick={() => setDateType(false)}>Travel</button>
                        </div>
                    </div>

                    <div className="field field-rangepicker-wrap" style={{ gridColumn: '4 / span 5', gridRow: 1 }}>
                        <div className="field-label">Date range</div>
                        <Form.Item name="dateRange" rules={[
                            { required: true, message: 'Please select a date range' },
                            () => ({
                                validator(_, value) {
                                    if (!value || (value[1].diff(value[0], "days") <= allowedMaxDaysRange)) return Promise.resolve();
                                    return Promise.reject(new Error(`Please select a date range within ${allowedMaxDaysRange} days`));
                                },
                            }),
                        ]}>
                            <RangePicker ranges={getRangeOptions(allowedMaxDaysRange)} format="DD MMM YYYY" />
                        </Form.Item>
                    </div>

                    <div className="filter-actions" style={{ gridColumn: '1 / -1', gridRow: 2, display: 'flex', gap: 8, justifyContent: 'flex-start', alignItems: 'flex-end' }}>
                        <button type="button" className="btn" onClick={onReset} disabled={running}>Reset</button>
                        <button type="submit" className="btn primary" disabled={running}>
                            {Icon.run}{running ? 'Searching…' : 'Run report'}
                        </button>
                    </div>
                </div>
            </Form>
        </div>
    );
}

function RefineBar({
    agentOptions, selectedAgents, isSelectAllAgents, onAgentChange,
    scheduleOptions, selectedSchedules, isSelectAllSchedules, onScheduleChange,
    statusValue, onStatusChange,
}) {
    const allAgents = useMemo(() => {
        if (!isValidArray(agentOptions)) return [];
        return [...new Set(agentOptions.map(a => (a && a.name) || a).filter(Boolean))].sort();
    }, [agentOptions]);

    const allSchedules = useMemo(
        () => isValidArray(scheduleOptions) ? scheduleOptions : [],
        [scheduleOptions]
    );

    const handleAgentChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allAgents.length > 0 && selected.length >= allAgents.length);
        onAgentChange({ agentName: selectAll ? [] : selected, isSelectAllAgents: selectAll });
    };

    const handleScheduleChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allSchedules.length > 0 && selected.length >= allSchedules.length);
        onScheduleChange({ schedules: selectAll ? [] : selected, isSelectAllSchedules: selectAll });
    };

    const agentValue = isSelectAllAgents ? [] : (selectedAgents || []);
    const scheduleValue = isSelectAllSchedules ? [] : (selectedSchedules || []);
    const hasActiveFilter = !isSelectAllAgents || !isSelectAllSchedules || statusValue !== both;

    return (
        <div className="refine-bar">
            <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                <div className="field-label">Filter by schedule</div>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={allSchedules.length === 0 ? 'No schedules in result' : 'All schedules'}
                    value={scheduleValue}
                    onChange={handleScheduleChange}
                    maxTagCount={2}
                    maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                    optionFilterProp="children"
                    filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={allSchedules.length === 0}
                    style={{ width: '100%' }}
                >
                    {allSchedules.map(desc => <Option key={desc} value={desc}>{desc}</Option>)}
                </Select>
            </div>
            <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                <div className="field-label">Filter by agent</div>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={allAgents.length === 0 ? 'No agents in result' : 'All agents'}
                    value={agentValue}
                    onChange={handleAgentChange}
                    maxTagCount={2}
                    maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                    optionFilterProp="children"
                    filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={allAgents.length === 0}
                    style={{ width: '100%' }}
                >
                    {allAgents.map(name => <Option key={name} value={name}>{name}</Option>)}
                </Select>
            </div>
            <div className="field field-select-wrap" style={{ width: 200 }}>
                <div className="field-label">Traveller status</div>
                <Select value={statusValue} onChange={onStatusChange} style={{ width: '100%' }}>
                    <Option value={both}>Blocked/Released</Option>
                    <Option value="Blocked">Blocked</Option>
                    <Option value="Released">Released</Option>
                </Select>
            </div>
            {hasActiveFilter && (
                <button type="button" className="btn" onClick={() => {
                    handleAgentChange([]);
                    handleScheduleChange([]);
                    onStatusChange(both);
                }}>Clear</button>
            )}
        </div>
    );
}

function FilterSummaryChips({ filters, generatedAt, scheduleLabel, agentLabel, statusLabel, hasReport }) {
    if (!hasReport) return null;
    const dateLabel = filters.fromDate && filters.toDate ? `${filters.fromDate} – ${filters.toDate}` : '—';
    return (
        <div className="filter-summary">
            <span className="chip"><span className="label">{filters.isBookingDate ? 'Booking' : 'Travel'}</span><strong>{dateLabel}</strong></span>
            <span className="chip"><span className="label">Schedule</span><strong>{scheduleLabel || 'All'}</strong></span>
            <span className="chip"><span className="label">Agent</span><strong>{agentLabel || 'All'}</strong></span>
            <span className="chip"><span className="label">Status</span><strong>{statusLabel}</strong></span>
            {generatedAt && <span className="gen">Generated {generatedAt}</span>}
        </div>
    );
}

function TxDetailGrid({ record, vehicleNumberById = {} }) {
    const seatStr = record.seat_number || '';
    const bookingTime = record.booking_time ? getLocalDateAndTime(record.booking_time) : '—';
    const travelDate = isValid(record.start_date) ? getLocalDate(convertDateStringToDate(record.start_date.split('T')[0])) : '—';
    const releaseTime = isValid(record.release_time) ? getLocalDate(convertDateStringToDate(record.release_time.split('T')[0])) : '—';
    const { label: statusLabel } = getStatusMeta(record);

    const ticketFare = record.fare || 0;
    const gst = (record.cgst || 0) + (record.sgst || 0);
    const total = record.ticket_cost || 0;
    const discount = record.direct_discount || 0;
    const amountPaid = record.amount_paid || 0;
    const commission = record.commission || 0;
    const tds = record.tds || 0;
    const otaGst = (isValid(record.privilege) && record.privilege == "OTA") ? gst : 0;
    const netAmount = amountPaid - (commission - tds) - otaGst;

    return (
        <div className="tx-detail-grid">
            <div className="tx-detail-section">
                <div className="l">Blocking Details</div>
                <div className="detail-row"><span className="k">Booking time</span><span className="v">{bookingTime}</span></div>
                <div className="detail-row"><span className="k">Email</span><span className="v text">{record.email_id || '—'}</span></div>
                <div className="detail-row"><span className="k">Phone</span><span className="v">{record.phone_number || '—'}</span></div>
                <div className="detail-row"><span className="k">Seat nos</span><span className="v">{seatStr || '—'}</span></div>
                {record.description && <div className="detail-row"><span className="k">Trip</span><span className="v text">{record.description}</span></div>}
                <div className="detail-row"><span className="k">Vehicle</span><span className="v text">{vehicleNumberById[record.vehicle_id] || record.vehicle_number || '—'}</span></div>
                <div className="detail-row"><span className="k">Travel date</span><span className="v">{travelDate}</span></div>
            </div>
            <div className="tx-detail-section">
                <div className="l">Financial Breakdown</div>
                <div className="detail-row"><span className="k">Ticket fare (A)</span><span className="v">{fmtINRpaise(ticketFare)}</span></div>
                <div className="detail-row"><span className="k">GST (B)</span><span className="v">{fmtINRpaise(gst)}</span></div>
                <div className="detail-row"><span className="k">Total amount (A+B)</span><span className="v">{fmtINRpaise(total)}</span></div>
                <div className="detail-row"><span className="k">Discount (D)</span><span className="v">{fmtINRpaise(discount)}</span></div>
                <div className="detail-row"><span className="k">Amount paid (A+B−D)</span><span className="v">{fmtINRpaise(amountPaid)}</span></div>
                <div className="detail-row"><span className="k">Commission (F)</span><span className="v">{fmtINRpaise(commission)}</span></div>
                <div className="detail-row"><span className="k">TDS (G)</span><span className="v">{fmtINRpaise(tds)}</span></div>
                <div className="detail-row bold"><span className="k">Net amount (Amount paid − (F − G) − OTA GST)</span><span className="v">{fmtINRpaise(netAmount)}</span></div>
            </div>
            <div className="tx-detail-section">
                <div className="l">Release Details</div>
                <div className="detail-row"><span className="k">Status</span><span className="v text">{statusLabel}</span></div>
                <div className="detail-row"><span className="k">Release time</span><span className="v">{releaseTime}</span></div>
                <div className="detail-row"><span className="k">Released by</span><span className="v text">{record.updated_by || '—'}</span></div>
                {global.useRemarks === 1 && <div className="detail-row"><span className="k">Remarks</span><span className="v text">{record.remarks || '—'}</span></div>}
                <div className="detail-row bold"><span className="k">Fare</span><span className="v">{fmtINR(record.discounted_fare)}</span></div>
            </div>
        </div>
    );
}

// ----- Display columns for the compact transactions table -----
const buildDisplayColumns = (vehicleNumberById = {}) => [
    {
        title: 'PNR', key: 'pnr', dataIndex: 'pnr',
        render: (text) => <span className="tx-pnr">{text || '—'}</span>,
    },
    {
        title: 'Agency', key: 'name', dataIndex: 'name',
        render: (text) => (
            <span className="tx-agent">
                <span className={`dot ${getChannelClass(text)}`}></span>{text || '—'}
            </span>
        ),
    },
    {
        title: 'Email', key: 'email_id', dataIndex: 'email_id',
        render: (text) => <span className="tx-route"><span className="name" style={{ marginTop: 0 }}>{text || '—'}</span></span>,
    },
    {
        title: 'Phone No', key: 'phone_number', dataIndex: 'phone_number',
        render: (text) => <span className="tx-date">{text || '—'}</span>,
    },
    {
        title: 'Trip', key: 'description', dataIndex: 'description',
        render: (text, record) => (
            <div className="tx-route">
                <div className="ot">{text || '—'}</div>
                {record.full_name && <div className="name">{record.full_name}</div>}
            </div>
        ),
    },
    {
        title: 'Travel', key: 'start_date',
        render: (_t, record) => isValid(record.start_date)
            ? <span className="tx-date">{getLocalDate(convertDateStringToDate(record.start_date.split('T')[0]))}</span>
            : <span className="tx-date">—</span>,
    },
    {
        title: 'Vehicle', key: 'vehicle_number', dataIndex: 'vehicle_id',
        render: (_t, record) => <span className="tx-date">{vehicleNumberById[record.vehicle_id] || record.vehicle_number || '—'}</span>,
    },
    {
        title: 'Seats', key: 'seat_number', dataIndex: 'seat_number',
        render: (text) => {
            const seats = String(text || '').split(',').map(s => s.trim()).filter(Boolean);
            if (seats.length === 0) return <span className="tx-seat-pill">—</span>;
            return <div className="tx-seats">{seats.map(s => <span key={s} className="tx-seat-pill">{s}</span>)}</div>;
        },
    },
    {
        title: 'Status', key: 'traveller_status', dataIndex: 'traveller_status',
        render: (_t, record) => {
            const { cls, label } = getStatusMeta(record);
            return <span className={`tx-status ${cls}`}>{label}</span>;
        },
    },
    {
        title: 'Release time', key: 'release_time', dataIndex: 'release_time',
        render: (_t, record) => isValid(record.release_time)
            ? <span className="tx-date">{getLocalDate(convertDateStringToDate(record.release_time.split('T')[0]))}</span>
            : <span className="tx-date">—</span>,
    },
    {
        title: 'Released by', key: 'updated_by', dataIndex: 'updated_by',
        render: (text) => <span className="tx-agent">{text || '—'}</span>,
    },
    {
        title: 'Fare', key: 'discounted_fare', dataIndex: 'discounted_fare',
        align: 'right', className: 'col-amount',
        sorter: (a, b) => (Number(a.discounted_fare) || 0) - (Number(b.discounted_fare) || 0),
        render: (text) => <span className="tx-amount">{fmtINR(text)}</span>,
    },
    ...(global.useRemarks === 1
        ? [{
            title: 'Remarks', key: 'remarks', dataIndex: 'remarks',
            render: (text) => <span className="tx-remarks">{text || '—'}</span>,
        }]
        : []),
];

function TransactionsCard({ tickets, page, setPage, paginationSize, setPaginationSize, vehicleNumberById = {} }) {
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('travel_recent');

    const data = useMemo(() => {
        if (!isValidArray(tickets)) return [];
        const q = search.trim().toLowerCase();
        let r = tickets;
        if (q) {
            r = r.filter(x => {
                const fields = [x.pnr, x.name, x.email_id, x.phone_number, x.description, x.seat_number, vehicleNumberById[x.vehicle_id]];
                return fields.some(f => f && String(f).toLowerCase().includes(q));
            });
        }
        const sorted = [...r];
        switch (sort) {
            case 'travel_recent':
                sorted.sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || ''))); break;
            case 'fare_high':
                sorted.sort((a, b) => (Number(b.discounted_fare) || 0) - (Number(a.discounted_fare) || 0)); break;
            case 'pnr':
                sorted.sort((a, b) => String(a.pnr || '').localeCompare(String(b.pnr || ''))); break;
            default: break;
        }
        return sorted;
    }, [tickets, search, sort]);

    const columns = useMemo(() => buildDisplayColumns(vehicleNumberById), [vehicleNumberById]);

    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Blocked Tickets</div>
                <div className="tabs">
                    <button type="button" className="active">
                        Tickets <span className="count">{Number(data.length).toLocaleString('en-IN')}</span>
                    </button>
                </div>
            </div>
            <div className="card-sub">
                Seats that were blocked and/or released for the selected period. Tap a row to see the full booking and release details.
            </div>

            <div className="table-toolbar">
                <div className="search">
                    {Icon.search}
                    <input
                        placeholder="Search by PNR, agency, email, phone, trip, vehicle…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="table-toolbar-right">
                    <span>Sort:</span>
                    <Select value={sort} onChange={setSort} size="small" style={{ width: 200 }} bordered={true}>
                        <Option value="travel_recent">Travel date (recent)</Option>
                        <Option value="fare_high">Highest fare</Option>
                        <Option value="pnr">PNR</Option>
                    </Select>
                </div>
            </div>

            <Table
                className="tx-table"
                rowKey={(r, idx) => `${r.pnr || ''}-${r.seat_number || ''}-${idx}`}
                columns={columns}
                dataSource={data}
                pagination={getPaginationDetails(true, setPage, paginationSize, setPaginationSize)}
                expandable={{
                    expandedRowRender: (record) => <TxDetailGrid record={record} vehicleNumberById={vehicleNumberById} />,
                    expandIconColumnIndex: -1,
                    expandRowByClick: true,
                    rowExpandable: () => true,
                }}
                locale={{ emptyText: <div className="empty-state">No blocked tickets match the current filters.</div> }}
                size="middle"
            />
        </div>
    );
}

// ============================================================
// Main component
// ============================================================
export const BlockedSeatsReport = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [showReport, setShowReport] = useState(false);
    const [running, setRunning] = useState(false);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [dateType, setDateType] = useState(false);   // false = Travel, true = Booking
    const [blockedTickets, setBlockedTickets] = useState(undefined);

    const [page, setPage] = useState(1);
    const [paginationSize, setPaginationSize] = useState(getDefaultPageSize);
    const [vehicleNumberById, setVehicleNumberById] = useState({});

    // Vehicle id → number map for display lookups (same approach as the sales report).
    useEffect(() => {
        getVechicles((vehicleData) => {
            setVehicleNumberById(vehicleData.reduce((acc, v) => { acc[v.id] = v.vechicle_no; return acc; }, {}));
        });
    }, []);

    const [filters, setFilters] = useState({
        fromDate: undefined, toDate: undefined, isBookingDate: false,
        agentNames: undefined, schedules: undefined,
        agentName: [], isSelectAllAgents: true,
        selectedSchedules: [], isSelectAllSchedules: true,
        travellerStatus: both,
    });

    const setLoading = (bool) => dispatch(loadingAction(bool));

    const onSuccess = (data) => {
        const schedules = getSchedules(data.blockedTransactions);
        setBlockedTickets(data.blockedTransactions);
        setFilters((f) => ({
            ...f,
            agentNames: data.agents,
            schedules,
            agentName: [], isSelectAllAgents: true,
            selectedSchedules: [], isSelectAllSchedules: true,
            travellerStatus: both,
        }));
        setGeneratedAt(getLocalDateAndTime(new Date()));
        setShowReport(true);
        setRunning(false);
        setLoading(false);
    };

    const onFailure = () => {
        setRunning(false);
        setLoading(false);
        message.error('Failed to load blocked seats report');
    };

    const onRunReport = (values) => {
        const { dateRange, isBooking } = values;
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        setShowReport(false);
        setRunning(true);
        setLoading(true);
        setFilters((f) => ({
            ...f,
            fromDate, toDate, isBookingDate: !!isBooking,
            agentNames: undefined, schedules: undefined,
            agentName: [], isSelectAllAgents: true,
            selectedSchedules: [], isSelectAllSchedules: true,
            travellerStatus: both,
        }));
        fetchBlockedSheet(fromDate, toDate, isBooking, onSuccess, onFailure);
    };

    const onReset = () => {
        form.resetFields();
        setDateType(false);
        setBlockedTickets(undefined);
        setShowReport(false);
        setGeneratedAt(null);
        setFilters({
            fromDate: undefined, toDate: undefined, isBookingDate: false,
            agentNames: undefined, schedules: undefined,
            agentName: [], isSelectAllAgents: true,
            selectedSchedules: [], isSelectAllSchedules: true,
            travellerStatus: both,
        });
    };

    // Live client-side filtering — schedule + agent + traveller status.
    const filteredBlockedTickets = useMemo(
        () => getFilterDetails({
            scheduleDesc: filters.isSelectAllSchedules ? [] : filters.selectedSchedules,
            agentName: filters.isSelectAllAgents ? [] : filters.agentName,
            travellerStatus: filters.travellerStatus,
        }, blockedTickets),
        [blockedTickets, filters.selectedSchedules, filters.isSelectAllSchedules, filters.agentName, filters.isSelectAllAgents, filters.travellerStatus]
    );

    const scheduleLabel = filters.isSelectAllSchedules ? 'All'
        : (isValidArray(filters.selectedSchedules)
            ? (filters.selectedSchedules.length <= 2 ? filters.selectedSchedules.join(', ') : `${filters.selectedSchedules.length} selected`)
            : 'All');
    const agentLabel = filters.isSelectAllAgents ? 'All'
        : (isValidArray(filters.agentName)
            ? (filters.agentName.length <= 2 ? filters.agentName.join(', ') : `${filters.agentName.length} selected`)
            : 'All');
    const statusLabel = filters.travellerStatus === both ? 'Blocked/Released' : filters.travellerStatus;

    const onExport = () => {
        const exportColumns = getBlockedTicketsColumns(1, getDefaultPageSize, vehicleNumberById);
        generateJsonToExport(filters, filteredBlockedTickets, exportColumns);
    };

    const printerNode = (
        <Printer
            showComponent={false}
            zoomValue={0.8}
            renderComponent={() => (
                <RenderReport
                    fromDate={filters.fromDate}
                    toDate={filters.toDate}
                    isBookingDate={filters.isBookingDate}
                    agentName={filters.agentName}
                    isSelectAllAgents={filters.isSelectAllAgents}
                    blockedTickets={filteredBlockedTickets}
                    pagination={false}
                    vehicleNumberById={vehicleNumberById}
                />
            )}
        />
    );

    return (
        <div className="report-inner-div sales-report-v2">
            <PageHeader onExport={onExport} canExport={showReport} printerNode={printerNode} />
            <FilterCard
                form={form}
                dateType={dateType}
                setDateType={setDateType}
                onSubmit={onRunReport}
                onReset={onReset}
                running={running}
            />
            {showReport && (
                <RefineBar
                    agentOptions={filters.agentNames}
                    selectedAgents={filters.agentName}
                    isSelectAllAgents={filters.isSelectAllAgents}
                    onAgentChange={({ agentName, isSelectAllAgents }) => setFilters((f) => ({ ...f, agentName, isSelectAllAgents }))}
                    scheduleOptions={filters.schedules}
                    selectedSchedules={filters.selectedSchedules}
                    isSelectAllSchedules={filters.isSelectAllSchedules}
                    onScheduleChange={({ schedules, isSelectAllSchedules }) => setFilters((f) => ({ ...f, selectedSchedules: schedules, isSelectAllSchedules }))}
                    statusValue={filters.travellerStatus}
                    onStatusChange={(travellerStatus) => setFilters((f) => ({ ...f, travellerStatus }))}
                />
            )}
            <FilterSummaryChips
                filters={filters}
                generatedAt={generatedAt}
                scheduleLabel={scheduleLabel}
                agentLabel={agentLabel}
                statusLabel={statusLabel}
                hasReport={showReport}
            />
            {showReport && (
                <TransactionsCard
                    tickets={filteredBlockedTickets}
                    page={page}
                    setPage={setPage}
                    paginationSize={paginationSize}
                    setPaginationSize={setPaginationSize}
                    vehicleNumberById={vehicleNumberById}
                />
            )}
        </div>
    );
};

// ============================================================
// Legacy RenderReport — preserved for the Printer preview (zoom 0.5).
// Renders the original bordered-table layout.
// ============================================================
const RenderReport = ({ fromDate, toDate, isBookingDate, agentName, blockedTickets, pagination, isSelectAllAgents, vehicleNumberById = {} }) => {
    const [page, setPage] = useState(1);
    const [paginationSize, setPaginationSize] = useState(getDefaultPageSize);

    const blockedTicketsColumns = getBlockedTicketsColumns(page, paginationSize, vehicleNumberById);

    return (
        <div>
            <Row justify="center" gutter={[16, 16]}>
                <Col><Text strong>Blocked tickets report</Text></Col>
            </Row>
            <Row gutter={[16, 0]} justify={"space-between"}>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>From date : </Text>{fromDate}</Text>
                </Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>To date : </Text>{toDate}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 0]} justify={"space-between"}>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Type of date : </Text>{isBookingDate ? "Booking Date" : "Travel Date"}</Text>
                </Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}>
                    <Text><Text strong>Generated day/time : </Text>{getLocalDateAndTime(new Date())}</Text>
                </Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col>
                    <Text strong>
                        Agent :{' '}
                        {(() => {
                            const list = normalizeToArray(agentName);
                            if (!list || list.length === 0) return 'All agents';
                            if (isSelectAllAgents) return 'All agents';
                            if (list.length === 1) return list[0];
                            return `${list.length} agents selected`;
                        })()}
                    </Text>
                </Col>
            </Row>
            <Row gutter={[16, 16]} className="marginTop">
                <Col>
                    <Table
                        className="bordered-table"
                        columns={blockedTicketsColumns}
                        dataSource={blockedTickets}
                        pagination={getPaginationDetails(pagination, setPage, getDefaultPageSize, setPaginationSize)}
                    />
                </Col>
            </Row>
        </div>
    );
};
